/**
 * "Actual Impact" — the measured effect of a change someone applied, as a cell.
 *
 * The backend (services/optimizationImpactService.js) measures delivery either
 * side of the apply date over equal windows and hands back a compact fact. This
 * module turns that into the strings a table cell renders, and nothing else: it
 * computes no rate and no delta of its own, so the cell can never disagree with
 * the API or with the same numbers rendered into the optimizer's prompt.
 *
 * WHAT IT REFUSES TO SAY. Not "improved by" — up and down, with the numbers.
 * The measurement is an observed before/after over a window in which other
 * changes, pacing and seasonality also moved, so the cell reports the move and
 * flags when another applied change fell inside the same span. A cell that said
 * "+21% from this change" would be inventing the one thing the data cannot show.
 *
 * AN ABSENT MEASUREMENT IS NOT A ZERO. A row nobody applied has no cell; a row
 * applied yesterday says it is still measuring; a platform with no per-day
 * reporting says so. Rendering any of those as "0%" would read as "this change
 * did nothing", which is a different and untrue claim.
 */

/** Percent with one decimal and an explicit sign, or null when there is none. */
export function pct(value) {
  return Number.isFinite(value) ? `${value >= 0 ? '+' : ''}${(value * 100).toFixed(1)}%` : null;
}

/** A rate as a percentage; null when the window had no denominator. */
export function rate(value) {
  return Number.isFinite(value) ? `${(value * 100).toFixed(2)}%` : null;
}

/** A whole-number count with thousands separators; null when absent. */
export function count(value) {
  return Number.isFinite(value) ? Math.round(value).toLocaleString('en-US') : null;
}

/** Why a measurement is not a measurement yet, in the reader's terms. */
export const STATUS_TEXT = {
  measuring: 'Measuring — too few days since this was applied',
  baseline_unavailable: 'No stored delivery from before this change',
  insufficient_data: 'Too little delivery either side to compare',
  // Split from a single "no per-day delivery" line, which read as a platform
  // limitation on Google Ads and DV360 — where the platform reports days fine
  // and nothing had collected them. A reader who is told the wrong cause goes
  // looking for the wrong fix.
  no_delivery_history_stored: 'No delivery history stored yet for this campaign — '
    + 'the next analysis run will record it',
  platform_reports_no_daily: 'This platform reports no per-day delivery',
  no_daily_data: 'No per-day delivery recorded for this campaign',
  unknown_applied_at: 'Apply time was not recorded',
};

/** The three metrics, in the order the question names them. */
const METRICS = [
  { key: 'impressions_per_day', label: 'Delivery', unit: 'imp/day', format: count },
  { key: 'ctr', label: 'CTR', unit: null, format: rate },
  { key: 'conversion_rate', label: 'Conv. rate', unit: null, format: rate },
];

/**
 * One impact fact → what the cell should show.
 *
 * @param {Object|null} impact the API's compact fact for this row
 * @returns {null|{kind:'pending',text:string}|{kind:'measured',windowDays:?number,
 *   rows:Array<{key,label,before,after,pct,direction}>,confounders:number}}
 *   null means "this row has no applied change" — the cell stays empty.
 */
export function impactCell(impact) {
  if (!impact || typeof impact !== 'object') return null;
  if (impact.status !== 'measured') {
    const text = STATUS_TEXT[impact.status];
    return text ? { kind: 'pending', text } : null;
  }
  const rows = [];
  for (const m of METRICS) {
    const value = impact[m.key];
    if (!value || typeof value !== 'object') continue;
    const before = m.format(value.before);
    const after = m.format(value.after);
    // A metric with no denominator on either side is dropped rather than shown
    // as "n/a → n/a": the row already carries the two that could be measured.
    if (before === null && after === null) continue;
    rows.push({
      key: m.key,
      label: m.label,
      unit: m.unit,
      before: before ?? 'n/a',
      after: after ?? 'n/a',
      pct: pct(value.pct),
      direction: value.direction || null,
    });
  }
  if (!rows.length) return { kind: 'pending', text: STATUS_TEXT.insufficient_data };
  return {
    kind: 'measured',
    windowDays: Number.isInteger(impact.window_days) ? impact.window_days : null,
    rows,
    confounders: Number.isInteger(impact.confounders) ? impact.confounders : 0,
  };
}

/**
 * The colour for a direction.
 *
 * Green for up and red for down on CTR and conversion rate, where higher is
 * unambiguously better. DELIVERY IS NEUTRAL: a change that cut a wasteful
 * budget is supposed to reduce impressions, and painting that red would call a
 * success a failure. The reader gets the number and decides.
 */
export function directionColor(metricKey, direction) {
  if (metricKey === 'impressions_per_day' || !direction || direction === 'flat') return '#6b7280';
  return direction === 'up' ? '#0F7B5F' : '#b91c1c';
}

/** The arrow for a direction — paired with the colour, never alone. */
export function directionArrow(direction) {
  if (direction === 'up') return '▲';
  if (direction === 'down') return '▼';
  return '–';
}

/** One-line plain-text form, for a title attribute or a compact layout. */
export function impactText(impact) {
  const cell = impactCell(impact);
  if (!cell) return '';
  if (cell.kind === 'pending') return cell.text;
  const parts = cell.rows.map((r) => `${r.label} ${r.before} → ${r.after}${r.pct ? ` (${r.pct})` : ''}`);
  const span = cell.windowDays ? `${cell.windowDays}d before vs ${cell.windowDays}d after` : 'equal windows';
  const confounded = cell.confounders > 0
    ? ` — ${cell.confounders} other applied change(s) fall inside this span`
    : '';
  return `${span}: ${parts.join('; ')}${confounded}`;
}
